import {
    APPLICATION_SCROLLING_BEHAVIOR_MODE,
    APPLICATION_SCROLLING_BEHAVIOR_WHEN,
    GUIDANCE_CONTROL_ATTACHMENT,
    GUIDANCE_CONTROL_PERSISTENCE,
} from '../typings/application/ApplicationEnum';
import { LAYER_NODE_PERSIST } from '../typings/guide/LayerEnum';
import { TRACKING_DEFAULT } from './TrackingDefaults';
import {
    LOCATION_HREF,
    LANGUAGE_READ,
    DEFAULT_CONTENT_LANGUAGE,
    DEFAULT_USED_LOCALE,
} from '../ModelConstant';
import {
    OrganizationId,
    ApplicationId,
    ApplicationContextHelpMetadata,
    ApplicationContextHelpContent,
    ApplicationIdentityDefinition,
    ApplicationUserDefinition,
    ApplicationRoleDefinition,
    ApplicationLocalizationDefinition,
    ApplicationLanguage,
    ApplicationLanguageFallback,
    LocationContextDefinition,
    SelectorSystem,
    SelectorSystemAttributes,
    ApplicationContextHelpContainer,
    ApplicationAccessAclData,
    ApplicationAccessData,
    ApplicationGuidanceControl,
    ApplicationThemeDefinition,
    ApplicationThemeVariantFallback,
    ApplicationThemeVariantMainDefinition,
    ApplicationThemeVariantDefinition,
    ApplicationAgentIntegration,
    ApplicationScrollingBehavior,
    Company,
} from '../typings/application/Application';
import { Iso6391Locale, MultiTenant } from '../typings/model/Model';

import { NODE_CONTAINER_DEFAULT, LOCATION_DEFAULT } from './NodeDefaults';

const RECOMMENDATION_COUNT_DEFAULT = 3;
const RECOMMENDATION_MODEL_ID_DEFAULT = 'trends';

export const RECOMMENDATION_DEFAULT = {
    enabled: false,
    count: RECOMMENDATION_COUNT_DEFAULT,
    model: {
        id: RECOMMENDATION_MODEL_ID_DEFAULT,
    },
};

export const PATH_SELECTOR_SYSTEM_DEFAULT = {
    pattern: '',
    replacer: '',
};

export const SELECTOR_SYSTEM_ID_DEFAULT = {
    blacklist: '',
};

export const SELECTOR_SYSTEM_CLASS_DEFAULT = {
    blacklist: '',
};

export const SELECTOR_SYSTEM_CUSTOM_DEFAULT = {
    whitelist: '',
};

export const SELECTOR_SYSTEM_ATTRIBUTES_DEFAULT: SelectorSystemAttributes = {
    id: SELECTOR_SYSTEM_ID_DEFAULT,
    className: SELECTOR_SYSTEM_CLASS_DEFAULT,
    custom: SELECTOR_SYSTEM_CUSTOM_DEFAULT,
};

export const SELECTOR_SYSTEM_DEFAULT: SelectorSystem = {
    classOnly: PATH_SELECTOR_SYSTEM_DEFAULT,
    attributes: SELECTOR_SYSTEM_ATTRIBUTES_DEFAULT,
    excludedLocations: [],
    disabledLocationPaths: [],
};

export const LOCATION_CONTEXT_DEFAULT: LocationContextDefinition[] = [];

export const MULTI_TENANT_DEFAULT: MultiTenant = {
    pattern: LOCATION_HREF.NO_PATTERN,
};

export const APPLICATION_LANGUAGE_FALLBACK_DEFAULT: ApplicationLanguageFallback = {
    value: DEFAULT_CONTENT_LANGUAGE,
};

export const APPLICATION_LANGUAGE_DEFAULT: ApplicationLanguage = {
    /**
     * @todo when modelConstant will be TS remove assertion
     */
    usedLocales: [DEFAULT_USED_LOCALE as { code: Iso6391Locale; label: string }],
    readFrom: LANGUAGE_READ.BROWSER,
    fallbackLanguage: APPLICATION_LANGUAGE_FALLBACK_DEFAULT,
};

export const EDITOR_SETTINGS_DEFAULT = {
    defaults: {
        node: {
            navigation: {
                href: {
                    pattern: LOCATION_HREF.NO_PATTERN,
                },
                ignoreNavigation: false,
                ignoreQueryParameters: true,
                navigate: false,
            },
            position: {
                container: NODE_CONTAINER_DEFAULT,
                scrollToStep: true,
                updateOnScroll: false,
                notCovered: false,
            },
            persistStep: LAYER_NODE_PERSIST.ANY,
            addon: {
                name: '',
            },
        },
    },
};

export const USER_DEFINITION_DEFAULT: ApplicationUserDefinition = {
    sourceType: 'notSet',
    valuePath: '',
};

export const ROLE_DEFINITION_DEFAULT: ApplicationRoleDefinition = {
    sourceType: 'notSet',
    valuePath: '',
};

export const LOCALIZATION_DEFAULT: ApplicationLocalizationDefinition = {
    sourceType: '',
    valuePath: '',
};

export const IDENTITY_DEFAULT: ApplicationIdentityDefinition = {
    user: USER_DEFINITION_DEFAULT,
    role: ROLE_DEFINITION_DEFAULT,
};

export const NOT_SET_DEFAULT = 'notSet';

export const APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT: ApplicationThemeVariantMainDefinition = {
    sourceType: NOT_SET_DEFAULT,
    valuePath: '',
};

export const APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT: ApplicationThemeVariantFallback = {
    enabled: false,
    valuePath: '',
};

export const APPLICATION_THEME_VARIANT_DEFAULT: ApplicationThemeVariantDefinition = {
    enabled: false,
    main: APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT,
    fallback: APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT,
};

export const THEME_ID_DEFAULT = 'notSet';

export const APPLICATION_THEME_DEFAULT: ApplicationThemeDefinition = {
    themeVariant: APPLICATION_THEME_VARIANT_DEFAULT,
    selectedTheme: THEME_ID_DEFAULT,
};

export const CONTEXT_HELP_CONTENT_NOTIFICATION_DEFAULT = {
    enable: false,
    notificationText: {},
};

export const DEFAULT_ORGANIZATION_ID = 'not-yet';

export const DEFAULT_ROLE_ORG_ADMIN = 'ye-org-admin';

export const APPLICATION_GUIDANCE_CONTROL_DEFAULT: ApplicationGuidanceControl = {
    enabled: false,
    browserPersistence: GUIDANCE_CONTROL_PERSISTENCE.BROWSER_RESTART,
    flags: {
        switchVisualGuidance: {
            attachment: GUIDANCE_CONTROL_ATTACHMENT.CONTEXT_HELP,
            labelOn: {},
            labelOff: {},
        },
    },
};

export const CONTEXT_HELP_CONTENT_DEFAULT: ApplicationContextHelpContent = {
    journeyTrackersHeading: {},
    listOfToursHeading: {},
    listOfPlaylistsHeading: {},
    restartProgressQuestion: {},
    restartProgressQuestionNo: {},
    restartProgressQuestionYes: {},
    searchInputPlaceHolder: {},
    noResultsFound: {},
    notification: CONTEXT_HELP_CONTENT_NOTIFICATION_DEFAULT,
    listOfArticlesHeading: {},
    learnMoreOfArticle: {},
    recommendedJourneysHeading: {},
    guidanceControl: APPLICATION_GUIDANCE_CONTROL_DEFAULT,
    viewPlaylistsText: {},
};

export const THEME_POSITIONED = 'themePositioned';

export const CONTEXT_HELP_CONTAINER_DEFAULT: ApplicationContextHelpContainer = {
    location: LOCATION_DEFAULT,
    type: THEME_POSITIONED,
};

export const CONTEXT_HELP_METADATA_DEFAULT: ApplicationContextHelpMetadata = {
    directStart: false,
    navigate: true,
    content: CONTEXT_HELP_CONTENT_DEFAULT,
    container: CONTEXT_HELP_CONTAINER_DEFAULT,
    suppressVisibleGuides: false,
    articles: [],
    list: [],
};

export const CONFUSION_DIALOG_DEFAULT = {
    enabled: false,
    type: 'detection_based',
    content: {
        label: {},
        confirmText: {},
        cancelText: {},
    },
};

export const AGENT_INTEGRATION_DEFAULT: ApplicationAgentIntegration = {
    customScript: true,
    popups: false,
    internetExplorer: false,
    polyfill: false,
    genericApplication: true,
    startFromIFrame: false,
    ifcNamespace: true,
    enableEditorRedirect: true,
};

export const APPLICATION_MONKEYS_DEFAULT = {
    patchEventStopPropagation: false,
};

export const APPLICATION_JOURNEY_TRACKER_DEFAULT_ITEM = {
    content: {
        name: {},
    },
};

export const APPLICATION_ACCESS_ACL_DEFAULT: ApplicationAccessAclData = {
    admin: [],
    read: [],
    write: [],
};

export const APPLICATION_ACCESS_OWNER_DEFAULT = {
    id: 'unknown-user@yeself.com',
};

export const APPLICATION_ACCESS_DEFAULT: ApplicationAccessData = {
    acl: APPLICATION_ACCESS_ACL_DEFAULT,
    owner: APPLICATION_ACCESS_OWNER_DEFAULT,
};

export const APPLICATION_METADATA_DEFAULT = {
    integration: {
        agent: AGENT_INTEGRATION_DEFAULT,
    },
    contextHelp: CONTEXT_HELP_METADATA_DEFAULT,
    journeyTracker: {},
    language: APPLICATION_LANGUAGE_DEFAULT,
    tracking: TRACKING_DEFAULT,
};

export const APPLICATION_WYSIWYG_EDITOR_SETTINGS_DEFAULT = {
    fontFamilyList: {},
    fontSizeList: [],
    colorList: [],
};

export const APPLICATION_THEME_STYLE_SETTINGS_BORDERS_COLORS_DEFAULT = {
    firstColor: '#4ba82e',
    secondColor: '#ffffff',
};

export const APPLICATION_THEME_STYLE_SETTINGS_BORDERS_DEFAULT = {
    colors: {
        firstColor: '#4ba82e',
        secondColor: '#ffffff',
    },
};

export const APPLICATION_THEME_STYLE_SETTINGS_DEFAULT = {
    borders: APPLICATION_THEME_STYLE_SETTINGS_BORDERS_DEFAULT,
};

export const APPLICATION_WHITE_LABEL_DEFAULT = {
    enabled: false,
};

export const APPLICATION_SYSTEM_PAGES_V2_ENABLED_DEFAULT = true;

export const APPLICATION_SYSTEM_PAGES_V2_EVENTS_DEFAULT = false;

export const APPLICATION_KEYBOARD_CONTROLS_ENABLED_DEFAULT = false;

export const APPLICATION_DEFAULT = {
    id: 'no-application-id' as ApplicationId,
    organizationId: 'no-organization-id' as OrganizationId,
    originUrls: [],
    trustedDomains: [],
    name: '',
    metadata: APPLICATION_METADATA_DEFAULT,
    selectorSystem: SELECTOR_SYSTEM_DEFAULT,
    localization: LOCALIZATION_DEFAULT,
    identity: IDENTITY_DEFAULT,
    language: APPLICATION_LANGUAGE_DEFAULT,
    editorSettings: EDITOR_SETTINGS_DEFAULT,
    wysiwygEditorSettings: APPLICATION_WYSIWYG_EDITOR_SETTINGS_DEFAULT,
    location: {
        context: {},
    },
    theme: APPLICATION_THEME_DEFAULT,
    locationContext: LOCATION_CONTEXT_DEFAULT,
    multiTenant: MULTI_TENANT_DEFAULT,
    recommendation: RECOMMENDATION_DEFAULT,
    themeStyleSettings: APPLICATION_THEME_STYLE_SETTINGS_DEFAULT,
    systemPagesV2Enabled: APPLICATION_SYSTEM_PAGES_V2_ENABLED_DEFAULT,
    keyboardControlsEnabled: APPLICATION_KEYBOARD_CONTROLS_ENABLED_DEFAULT,
};

export const EVENT_METADATA_DEFAULT = {
    metadata: {
        location: {},
        effect: {
            name: 'yeself-effect-none',
        },
    },
};

export const EVENT_DEFAULT = {
    type: '',
    metadata: EVENT_METADATA_DEFAULT,
    target: '',
    message: '',
    fired: false,
};

export const APPLICATION_CLIENT_CONFIG_DEFAULT = {
    enabled: false,
    value: {},
};

export const APPLICATION_SCROLLING_BEHAVIOR_DEFAULT: ApplicationScrollingBehavior = {
    when: APPLICATION_SCROLLING_BEHAVIOR_WHEN.IF_NEEDED,
    mode: APPLICATION_SCROLLING_BEHAVIOR_MODE.LIBRARY,
};

export const APPLICATION_SYSTEM_DEFAULT = {
    slug: '',
};

export const APPLICATION_COMPANY_DEFAULT: Company = {
    id: null,
};

export const APPLICATION_ADDONS_DEFAULT = {
    installed: [],
};
